package org.example.project.enginevertrieb

import org.example.project.dashboard.PopupEngineCard
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.jetbrains.compose.web.css.*
import kotlinx.coroutines.CancellationException
import androidx.compose.runtime.key
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.RollenService
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.META_HEADERS
import org.example.project.utils.All

private const val LEADTYPEN_VERWALTEN_OPTION = "__leadtypen_verwalten__"
private const val ICP_WERTE_VERWALTEN_OPTION = "__icp_werte_verwalten__"

@Composable
fun Vertriebsindex() {
    var eintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var zusatzHeaders by remember { mutableStateOf<List<VertriebsindexApi.ZusatzHeader>>(emptyList()) }
    var headerDialogOpen by remember { mutableStateOf(false) }
    var headerFehler by remember { mutableStateOf("") }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val scope = rememberCoroutineScope()
    var markierteFirmen by remember { mutableStateOf<Set<String>>(emptySet()) }
    val poolHinzufuegenLaeuft = remember { mutableStateOf(false) }
    var poolFehler by remember { mutableStateOf("") }
    var icpFehler by remember { mutableStateOf("") }
    var icpLaeuft by remember { mutableStateOf(false) }
    var leadTypFehler by remember { mutableStateOf("") }
    var leadTypLaeuft by remember { mutableStateOf(false) }
    var leadTypen by remember { mutableStateOf<List<VertriebsindexApi.LeadTypEintrag>>(emptyList()) }
    var leadTypenDialogOpen by remember { mutableStateOf(false) }
    var icpWerte by remember { mutableStateOf<List<VertriebsindexApi.IcpEintrag>>(emptyList()) }
    var icpWerteDialogOpen by remember { mutableStateOf(false) }
    var tabellenVersion by remember { mutableStateOf(0) }
    var stornierenIds by remember { mutableStateOf<List<String>>(emptyList()) }
    var stornierenLaeuft by remember { mutableStateOf(false) }
    var stornierenFehler by remember { mutableStateOf("") }
    var aktualisierenFehler by remember { mutableStateOf("") }
    var importInfo by remember { mutableStateOf("") }
    var importLaeuft by remember { mutableStateOf(false) }
    var infoLeadId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try { zusatzHeaders = VertriebsindexApi.loadHeaders() }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { headerFehler = e.message ?: "Header konnten nicht geladen werden." }
        try { leadTypen = VertriebsindexApi.loadLeadtypen() }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { leadTypFehler = e.message ?: "Leadtypen konnten nicht geladen werden." }
        try { icpWerte = VertriebsindexApi.loadIcpWerte() }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { icpFehler = e.message ?: "ICP-Werte konnten nicht geladen werden." }
    }

    fun checkbox(value: Boolean) = if (value) "✅" else "⬜"
    fun checked(value: String) = value.equals("true", true) || value == "1" || value.equals("ja", true) || value == "✅"

    suspend fun reloadVertriebsindex(vollstaendig: Boolean = false) {
        if (vollstaendig) letzteFilter = emptyMap()
        val filters = letzteFilter
        eintraege = if (filters == null) {
            emptyList()
        } else {
            VertriebsindexApi.searchEintraege(filters)
                .filter { it.status == "Angelegt" || it.status == "Storniert" }
                .sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        }
        if (vollstaendig) {
            markierteFirmen = emptySet()
            tabellenVersion++
        }
        aktualisierenFehler = ""
    }

    fun updateEintraege(updated: List<All>) {
        val byId = updated.associateBy { it.id }
        eintraege = eintraege.map { byId[it.id] ?: it }
    }

    val sichtbareEintraege = remember(eintraege) {
        eintraege.filter { it.status == "Angelegt" || it.status == "Storniert" }
    }
    val eintraegeById = remember(sichtbareEintraege) { sichtbareEintraege.associateBy { it.id } }
    val selectedEintraege = remember(eintraegeById, markierteFirmen) {
        markierteFirmen.mapNotNull { eintraegeById[it] }
    }
    val selectedExists = selectedEintraege.size == markierteFirmen.size
    val selectedAngelegt = selectedExists && selectedEintraege.all { it.status == "Angelegt" }
    val tabellenDaten = remember(sichtbareEintraege, zusatzHeaders) {
        sichtbareEintraege.map { eintrag -> eintrag.id to vertriebRow(eintrag, zusatzHeaders) }
    }

    fun buildEintrag(rowId: String, cols: List<String>, existing: All?): All {
        val values = vertriebPflichtHeader.zip(cols).toMap()
        fun cell(header: String) = values[header].orEmpty().trim()
        val id = existing?.id.orEmpty()
        return All(
            id = id,
            leadTyp = existing?.leadTyp.orEmpty(),
            leadTypId = existing?.leadTypId.orEmpty(),
            leadQuelle = existing?.leadQuelle.orEmpty().ifBlank { "Web" },
            firma = cell("Firma"),
            kunde = cell("Firma"),
            strasse = cell("Straße"),
            hausnummer = cell("Hausnummer"),
            plz = cell("PLZ"),
            ort = cell("Ort"),
            webseite = cell("Webseite"),
            email = cell("E-Mail"),
            ansprechpartner = cell("Ansprechpartner"),
            ansprechpartnerPosition = cell("Position"),
            telefon = cell("Telefon"),
            mobil = cell("Mobil"),
            linkedin = cell("LinkedIn"),
            vertriebZusatzfelder = existing?.vertriebZusatzfelder.orEmpty() +
                zusatzHeaders.mapIndexed { index, header -> header.id to cols.getOrNull(vertriebPflichtHeader.size + index).orEmpty().trim() },
            anrede = existing?.anrede.orEmpty(),
            vorname = existing?.vorname.orEmpty(),
            nachname = existing?.nachname.orEmpty(),
            bezeichnung = existing?.status.orEmpty().ifBlank { existing?.bezeichnung.orEmpty() }.ifBlank { "Angelegt" },
            status = existing?.status.orEmpty().ifBlank { existing?.bezeichnung.orEmpty() }.ifBlank { "Angelegt" }
        )
    }

    val infoLead = eintraegeById[infoLeadId]
    if (headerDialogOpen) HeaderHinzufuegenDialog("vertriebsindex-header", zusatzHeaders, { headerDialogOpen = false }) {
        zusatzHeaders = it
        tabellenVersion++
    }
    if (leadTypenDialogOpen) VertriebLeadtypenDialog(
        onClose = { leadTypenDialogOpen = false },
        onChanged = { leadTypen = it }
    )
    if (icpWerteDialogOpen) VertriebIcpWerteDialog(
        onClose = { icpWerteDialogOpen = false },
        onChanged = { icpWerte = it }
    )
    if (infoLead != null) PopupEngineCard(
        windowKey = "vertriebsindex-info-${infoLead.id}", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 900, heightPx = 600, minWidthPx = 520, minHeightPx = 380,
        centered = true, onClose = { infoLeadId = null }
    ) {
        LeadInfoTable(infoLead, RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT)) { updated ->
            eintraege = eintraege.map { if (it.id == updated.id) updated else it }
        }
    }

    if (stornierenIds.isNotEmpty()) {
        Div({ style {
            position(Position.Fixed); top(0.px); left(0.px); width(100.vw); height(100.vh)
            backgroundColor(Color("rgba(0,0,0,0.55)"))
            property("z-index", (POPUP_ENGINE_MODAL_Z_INDEX - 1).toString())
        } })
        PopupEngineCard(
            windowKey = "vertriebsindex-stornieren", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
            widthPx = 640, heightPx = null, minWidthPx = 320, minHeightPx = 260,
            viewportAnchored = true, centered = true, resizable = false, showCloseIcon = false,
            onClose = { if (!stornierenLaeuft) stornierenIds = emptyList() }
        ) {
            Div({
                attr("role", "alertdialog"); attr("aria-modal", "true")
                attr("aria-label", "Firmen stornieren")
                style {
                    padding(40.px, 32.px); display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column); alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center); textAlign("center"); gap(24.px)
                    property("line-height", "1.6")
                }
            }) {
                Div { Text("Du bist dabei, ${stornierenIds.size} ${if (stornierenIds.size == 1) "Firma" else "Firmen"} zu stornieren.") }
                Div {
                    Div { Text(if (stornierenIds.size == 1) "Diese Firma wird aus der aktiven Akquise ausgeschlossen." else "Diese Firmen werden aus der aktiven Akquise ausgeschlossen.") }
                    Div { Text("Bitte bestätige den Vorgang mit STORNIEREN oder breche ab.") }
                }
                if (stornierenFehler.isNotBlank()) Div({ attr("role", "alert"); style { color(Color("#ffb4b4")) } }) { Text(stornierenFehler) }
                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); gap(16.px); paddingTop(8.px) } }) {
                    LumaticaButton("Abbrechen", { stornierenIds = emptyList() }, enabled = !stornierenLaeuft)
                    LumaticaButton("Stornieren", {
                        if (!stornierenLaeuft && RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT)) {
                            val ids = stornierenIds.toList()
                            stornierenLaeuft = true
                            scope.launch {
                                try {
                                    VertriebsindexApi.cancelFirmen(ids)
                                } catch (e: CancellationException) { throw e }
                                catch (e: Exception) {
                                    stornierenFehler = "Firmen konnten nicht storniert werden. Bitte aktualisieren und erneut versuchen."
                                    stornierenLaeuft = false
                                    return@launch
                                }
                                val cancelledIds = ids.toSet()
                                eintraege = eintraege.map { entry ->
                                    if (entry.id in cancelledIds) entry.copy(status = "Storniert", bezeichnung = "Storniert") else entry
                                }
                                markierteFirmen = emptySet()
                                stornierenIds = emptyList()
                                aktualisierenFehler = ""
                                try {
                                    reloadVertriebsindex()
                                } catch (e: CancellationException) { throw e }
                                catch (e: Exception) {
                                    aktualisierenFehler = "Firmen wurden storniert. Der Index konnte nicht neu geladen werden. Bitte aktualisieren."
                                }
                                stornierenLaeuft = false
                            }
                        }
                    }, variant = LumaticaButtonVariant.Action, enabled = !stornierenLaeuft)
                }
            }
        }
    }

    if (poolFehler.isNotBlank()) {
        Div({ attr("role", "alert") }) { Text(poolFehler) }
    }
    if (icpFehler.isNotBlank()) {
        Div({ attr("role", "alert") }) { Text(icpFehler) }
    }
    if (leadTypFehler.isNotBlank()) {
        Div({ attr("role", "alert") }) { Text(leadTypFehler) }
    }
    if (aktualisierenFehler.isNotBlank()) {
        Div({ attr("role", "alert") }) { Text(aktualisierenFehler) }
    }
    if (importInfo.isNotBlank()) Div({ attr("role", "status") }) { Text(importInfo) }
    if (headerFehler.isNotBlank()) Div({ attr("role", "alert") }) { Text(headerFehler) }
    key(tabellenVersion) {
    TabularScreen(
        screenKey = "Vertriebsindex",
        rollenKey = "Vertriebsindex",
        title = "Vertriebsindex",
        fixedLeadingColumns = vertriebFixedLeadingHeaders().take(4),
        fixedTrailingColumns = META_HEADERS,
        headers = vertriebTabularHeaders(zusatzHeaders),
        headerDisplayLabels = vertriebTabularLabels(zusatzHeaders),
        toggleColumns = zusatzHeaders.filter { it.typ == "CHECKBOX" }.map { it.id }.toSet(),
        models = sichtbareEintraege,
        syncExternalRowsWhileDirty = true,
        externalUpdateColumns = setOf("zustand", "lead_typ", "icp"),
        externalDataSyncDelayMs = 0L,
        onSelectedRowsChange = { markierteFirmen = it },
        extraNavButtons = listOf(headerButton(
            RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT)
        ) { headerDialogOpen = true }, TabularToolbarButton(
            label = "Information", icon = "images/info.svg",
            enabled = markierteFirmen.size == 1,
            onClick = { infoLeadId = markierteFirmen.singleOrNull() }
        ), TabularToolbarButton(
            label = "Lead Typ",
            icon = "images/filter.svg",
            enabled = !leadTypLaeuft && !icpLaeuft &&
                RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT),
            dropdownOptions = listOf(LEADTYPEN_VERWALTEN_OPTION to "Leadtypen anlegen") + leadTypen.map { it.leadTyp to it.leadTyp },
            selectedDropdownValue = selectedEintraege.map { it.leadTyp }.distinct().singleOrNull(),
            dropdownPlaceholder = "Lead Typ zuweisen",
            onDropdownChange = { value ->
                if (value == LEADTYPEN_VERWALTEN_OPTION) {
                    leadTypenDialogOpen = true
                } else if (value != null) {
                    if (markierteFirmen.isEmpty() || !selectedExists) {
                        leadTypFehler = "Bitte zuerst mindestens einen Lead im Index auswählen."
                    } else {
                        val ids = markierteFirmen.toList()
                        leadTypLaeuft = true
                        leadTypFehler = ""
                        scope.launch {
                            try {
                                updateEintraege(VertriebsindexApi.setLeadTyp(ids, value))
                            } catch (e: CancellationException) { throw e }
                            catch (e: Exception) { leadTypFehler = "Lead Typ konnte nicht gespeichert werden. Bitte aktualisieren." }
                            finally { leadTypLaeuft = false }
                        }
                    }
                }
            }
        ), TabularToolbarButton(
            label = "ICP",
            icon = "images/icp.svg",
            enabled = !icpLaeuft && !stornierenLaeuft &&
                RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT),
            dropdownOptions = listOf(ICP_WERTE_VERWALTEN_OPTION to "ICP-Werte anlegen", "" to "Leer") + icpWerte.map { it.icp to it.icp },
            selectedDropdownValue = selectedEintraege.map { it.icp }.distinct().singleOrNull(),
            dropdownPlaceholder = "ICP zuweisen",
            onDropdownChange = { value ->
                if (value == ICP_WERTE_VERWALTEN_OPTION) {
                    icpWerteDialogOpen = true
                } else if (value != null) {
                    if (markierteFirmen.isEmpty() || !selectedExists) {
                        icpFehler = "Bitte zuerst mindestens einen Lead im Index auswählen."
                    } else {
                        val ids = markierteFirmen.toList()
                        icpLaeuft = true
                        icpFehler = ""
                        scope.launch {
                            try {
                                updateEintraege(VertriebsindexApi.setIcp(ids, value))
                            } catch (e: CancellationException) { throw e }
                            catch (e: Exception) { icpFehler = "ICP konnte nicht gespeichert werden. Bitte aktualisieren." }
                            finally { icpLaeuft = false }
                        }
                    }
                }
            }
        ), TabularToolbarButton(
            label = "Zum Pool hinzufügen",
            icon = "images/add.svg",
            enabled = markierteFirmen.isNotEmpty() && !stornierenLaeuft && stornierenIds.isEmpty() &&
                selectedAngelegt &&
                RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT),
            onClick = poolClick@{
                if (poolHinzufuegenLaeuft.value) return@poolClick
                val ids = markierteFirmen.toList()
                if (ids.isEmpty()) return@poolClick
                val selectedIds = ids.toSet()
                poolHinzufuegenLaeuft.value = true
                poolFehler = ""
                scope.launch {
                    try {
                        VertriebsindexApi.addToVertriebspool(ids)
                        eintraege = eintraege.map { eintrag ->
                            if (eintrag.id in selectedIds) eintrag.copy(status = "Vorbereitet", bezeichnung = "Vorbereitet") else eintrag
                        }
                        markierteFirmen = emptySet()
                    } catch (e: CancellationException) { throw e }
                    catch (e: Exception) {
                        try {
                            reloadVertriebsindex()
                            poolFehler = "Übernahme konnte nicht bestätigt werden. Der Index wurde aktualisiert."
                        } catch (reloadError: CancellationException) { throw reloadError }
                        catch (_: Exception) {
                            poolFehler = "Übernahme konnte nicht bestätigt werden. Bitte den Index aktualisieren."
                        }
                    } finally {
                        poolHinzufuegenLaeuft.value = false
                    }
                }
            }
        ), TabularToolbarButton(
            label = "Firma stornieren",
            icon = "images/close.svg",
            enabled = markierteFirmen.isNotEmpty() && !stornierenLaeuft && stornierenIds.isEmpty() &&
                selectedAngelegt &&
                RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT),
            onClick = { stornierenFehler = ""; stornierenIds = markierteFirmen.toList() }
        )),
        data = tabellenDaten,
        editable = true,
        commitChangedRowsOnly = true,
        allowCreate = true,
        allowDelete = true,
        exportFileName = "vertriebsindex.csv",
        onCsvImportRequest = { csv ->
            if (!importLaeuft) {
                importLaeuft = true
                importInfo = ""
                scope.launch {
                    try {
                        val result = VertriebsindexApi.importCsv(csv)
                        reloadVertriebsindex(vollstaendig = true)
                        importInfo = "${result.imported} Leads importiert, ${result.duplicates} Duplikate übersprungen, ${result.skipped} Zeilen ohne Firma übersprungen."
                    } catch (e: CancellationException) { throw e }
                    catch (e: Exception) { aktualisierenFehler = e.message ?: "CSV konnte nicht importiert werden." }
                    finally { importLaeuft = false }
                }
            }
        },
        nonEditableCols = setOf(
            "Leadquelle", "lead_quelle",
            "Bereich", "vertriebBereich", "Zuständig", "akquiseZugewiesenAn",
            "ID",
            "Lead Typ",
            "Zustand",
            "Google Maps",
            "ICP"
        ),
        onRefresh = {
            scope.launch {
                zusatzHeaders = VertriebsindexApi.loadHeaders()
                reloadVertriebsindex(vollstaendig = true)
            }
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                reloadVertriebsindex()
            }
        },
        onCommit = {},
        onCommitConfirmed = { rows, complete ->
            scope.launch {
                try {
                    val existingById = eintraege.associateBy { it.id }
                    val savedRows = mutableListOf<All>()
                    rows.forEach { (rowId, cols) ->
                        val eintrag = buildEintrag(rowId, cols, existingById[rowId])
                        require(eintrag.firma.isNotBlank()) { "Firma ist ein Pflichtfeld." }
                        val saved = requireNotNull(VertriebsindexApi.saveTabellenEintrag(eintrag)) {
                            "Firma konnte nicht gespeichert werden."
                        }
                        savedRows += saved
                    }
                    aktualisierenFehler = ""
                    complete(true)
                    if (rows.any { (rowId, _) -> rowId !in existingById }) reloadVertriebsindex(vollstaendig = true)
                    else updateEintraege(savedRows)
                } catch (e: CancellationException) {
                    complete(false)
                    throw e
                } catch (e: Exception) {
                    aktualisierenFehler = e.message ?: "Firma konnte nicht gespeichert werden."
                    complete(false)
                }
            }
        },
        onDeleteRows = { ids ->
            VertriebsindexApi.deleteEintraege(ids)
            eintraege = eintraege.filterNot { it.id in ids }
            markierteFirmen = emptySet()
        }
    )
    }
}

internal fun vertriebGoogleMapsUrl(eintrag: All): String {
    val parts = listOf(eintrag.strasse, eintrag.hausnummer, eintrag.plz, eintrag.ort)
    if (parts.any { it.isBlank() }) return ""
    val address = "${eintrag.strasse.trim()} ${eintrag.hausnummer.trim()}, ${eintrag.plz.trim()} ${eintrag.ort.trim()}"
    return "https://www.google.com/maps/search/?api=1&query=" + (js("encodeURIComponent")(address) as String)
}
